import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, Sparkles, ShoppingCart } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTheme } from "@/lib/useTheme";
import { useCart } from "@/lib/cart";

const LINKS = [
  { label: "Home", target: "top" },
  { label: "Courses", target: "courses" },
  { label: "Packages", target: "packages" },
  { label: "1% Club", target: "one-percent-club" },
  { label: "FAQ", target: "faq" },
];

export default function Navigation() {
  const { theme, toggle } = useTheme();
  const { cart } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleNav = (e, target) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(target), 250);
    } else {
      scrollToId(target);
    }
  };

  const handleEnroll = (e) => {
    e.preventDefault();
    setOpen(false);
    const trigger = () => {
      const el = document.getElementById("premium-package");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      window.dispatchEvent(new CustomEvent("highlight-premium-package"));
    };
    if (location.pathname !== "/") {
      navigate("/?highlight=premium");
    } else {
      trigger();
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 bg-white/70 dark:bg-[#0F172A]/70 border-b border-slate-200/60 dark:border-white/5 glass"
          : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => handleNav(e, "top")}
          data-testid="brand-logo"
          className="flex items-center gap-2.5 group"
        >
          <img
            src="/logo-icon-96.png"
            srcSet="/logo-icon-64.png 1x, /logo-icon-128.png 2x, /logo-icon-256.png 3x"
            alt="SkillPreneur Academy"
            width="36"
            height="36"
            decoding="async"
            fetchPriority="high"
            className="h-9 w-9 shrink-0 aspect-square rounded-xl object-contain shadow-sm ring-1 ring-black/5 dark:ring-white/10"
          />
          <div className="flex flex-col leading-none">
            <span className="font-display font-semibold tracking-tight text-[15px] text-[#0F172A] dark:text-white">
              SkillPreneur
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Academy
            </span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <a
              key={l.target}
              href={`#${l.target}`}
              onClick={(e) => handleNav(e, l.target)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="px-3.5 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-[#0F172A] dark:hover:text-white rounded-full transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {cart.n > 0 && (
            <Link
              to="/cart"
              data-testid="nav-cart-link"
              aria-label={`Cart with ${cart.n} courses`}
              className="relative h-10 w-10 inline-flex items-center justify-center rounded-full border border-slate-200/80 dark:border-white/10 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 transition-colors"
            >
              <ShoppingCart className="h-4 w-4 text-[#0F172A] dark:text-white" />
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#06B6D4] text-[#0F172A] text-[10px] px-1 font-bold border-2 border-white dark:border-[#0F172A]"
              >
                {cart.n}
              </span>
            </Link>
          )}
          <Link
            to="/student/login"
            data-testid="nav-student-login"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 dark:border-white/10 bg-white/60 dark:bg-white/5 px-3.5 h-10 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-white/10 hover:border-[#06B6D4]/40 transition-colors"
          >
            Student Login
          </Link>
          {/* Admin login is intentionally NOT linked in the public nav — direct URL only: /admin/login */}

          <button
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            onClick={toggle}
            className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-slate-200/80 dark:border-white/10 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 transition-colors"
          >
            <AnimatePresence mode="wait" initial={false}>
              {theme === "dark" ? (
                <motion.span
                  key="sun"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="flex"
                >
                  <Sun className="h-4 w-4 text-[#F8FAFC]" />
                </motion.span>
              ) : (
                <motion.span
                  key="moon"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="flex"
                >
                  <Moon className="h-4 w-4 text-[#0F172A]" />
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <motion.button
            type="button"
            onClick={handleEnroll}
            data-testid="nav-cta-enroll"
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            className="hidden sm:inline-flex relative items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#0F172A] via-[#1E3A8A] to-[#06B6D4] shadow-[0_8px_24px_-8px_rgba(6,182,212,0.55)] hover:shadow-[0_14px_36px_-10px_rgba(6,182,212,0.75)] hover:brightness-110 transition-all duration-300 overflow-hidden"
          >
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700" />
            <Sparkles className="h-3.5 w-3.5" />
            <span className="relative">Enroll Now</span>
          </motion.button>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-slate-200/80 dark:border-white/10"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22 }}
            data-testid="mobile-menu"
            className="md:hidden mx-6 mt-3 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white/95 dark:bg-[#0F172A]/95 glass p-4 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)]"
          >
            <div className="flex flex-col">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.target}
                  href={`#${l.target}`}
                  onClick={(e) => handleNav(e, l.target)}
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="px-3 py-3 text-base font-medium text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                >
                  {l.label}
                </motion.a>
              ))}
              <button
                type="button"
                onClick={handleEnroll}
                data-testid="mobile-cta-enroll"
                className="mt-3 inline-flex justify-center items-center gap-1.5 rounded-full px-5 py-3 text-sm font-semibold text-white bg-gradient-to-r from-[#0F172A] via-[#1E3A8A] to-[#06B6D4] shadow-[0_8px_24px_-8px_rgba(6,182,212,0.55)] hover:brightness-110 transition-all duration-300"
              >
                <Sparkles className="h-4 w-4" />
                Enroll Now
              </button>

              <Link
                to="/student/login"
                onClick={() => setOpen(false)}
                data-testid="mobile-student-login"
                className="mt-3 inline-flex justify-center items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 dark:text-slate-300 hover:border-[#06B6D4]/40 transition-colors"
              >
                Student Login
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
